import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useTestContext } from '../../../context/testContext';
import { usePostMessage } from '../../../context/messageContext';
import { Keypad, SessionActions, ToggleGroup, breakpoints, colors, fonts } from '../../../ui';
import {
  buildIshiharaSequence,
  formatIshiharaAnswer,
  ishiharaPlateImage,
  isKeypadPlate,
  ISHIHARA_UNCLEAR,
  scoreIshiharaAnswer,
  shouldEndIshiharaEarly,
} from '../../../helpers/ishihara';
import { StepBody, Label, Note, useConfirmLeave, useTestStepSync } from '../testScreenKit';
import ColourVisionResults from './colour-vision-results';

// Colour Vision / Ishihara (Figma NEW session/12 middle, landscape 22 right, video hidden 23 left).
// The patient screen shows the plate full size ({type:'ishiharaPlate'} messages); the clinician types
// what the patient reads on the keypad ([C] clears, [n] = nothing seen) and records it with Next.
// Scoring, plate order and the stored result_data are the same as the async pre-screening test.

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(170px, 210px);
  gap: 24px 40px;
  align-items: center;
  flex: 1;
  min-height: 0;
  @media (max-width: ${breakpoints.phone - 1}px) {
    grid-template-columns: minmax(0, 1fr) 150px;
    gap: 12px;
  }
`;

const Plate = styled.img`
  display: block;
  width: 100%;
  max-width: min(420px, 52vh);
  aspect-ratio: 1;
  object-fit: contain;
  justify-self: center;
  @media (max-width: ${breakpoints.phone - 1}px) {
    max-width: 100%;
  }
`;

const Side = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const Progress = styled.div`
  font-family: ${fonts.family};
  font-size: 13px;
  font-weight: 600;
  color: ${colors.slate[500]};
  text-align: center;
`;

const MAX_DIGITS = 2;

const ColourVision = ({ setTitle, setActiveTab }) => {
  const { testData, updateTestData } = useTestContext();
  const postMessage = usePostMessage();
  const confirmLeave = useConfirmLeave();
  const [plates] = useState(() => buildIshiharaSequence());
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [entry, setEntry] = useState('');
  const [step, setStep] = useState('test');
  useTestStepSync(testData.testKey, step === 'test' ? `plate-${index + 1}` : 'results');

  const plate = plates[index];
  const keypad = plate ? isKeypadPlate(plate) : true;

  useEffect(() => {
    setTitle(step === 'results' ? 'Colour Vision Test Results' : 'Colour Vision Test');
  }, [setTitle, step]);

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  // Show the current plate on the patient's screen.
  useEffect(() => {
    if (step !== 'test' || !plate) return;
    postMessage({ type: 'ishiharaPlate', content: { plateId: plate.id, index, total: plates.length, instructions: plate.custom_instructions || null } });
  }, [postMessage, plate, index, plates.length, step]);

  const onKey = useCallback((key) => {
    if (key === 'clear') return setEntry('');
    if (key === 'none') return setEntry(ISHIHARA_UNCLEAR);
    setEntry((current) => {
      const digits = current === ISHIHARA_UNCLEAR ? '' : current;
      return digits.length >= MAX_DIGITS ? digits : digits + key;
    });
  }, []);

  const finish = (finalAnswers) => {
    setAnswers(finalAnswers);
    postMessage({ type: 'endOfTest' });
    setStep('results');
  };

  const record = () => {
    if (!plate || entry === '') return;
    const nextAnswers = { ...answers, [plate.id]: scoreIshiharaAnswer(plate, entry) };
    setEntry('');
    if (shouldEndIshiharaEarly(nextAnswers) || index + 1 >= plates.length) return finish(nextAnswers);
    setAnswers(nextAnswers);
    setIndex(index + 1);
  };

  const back = () => {
    if (index === 0) return confirmLeave().then(() => updateTestData({ testKey: null, testInProgress: false }));
    const previous = plates[index - 1];
    const { [previous.id]: previousAnswer, ...rest } = answers;
    setAnswers(rest);
    setEntry(previousAnswer?.selected || '');
    setIndex(index - 1);
  };

  const restart = () =>
    confirmLeave('restart').then(() => {
      setAnswers({});
      setEntry('');
      setIndex(0);
      setStep('test');
    });

  const entryLabel = useMemo(() => (entry === ISHIHARA_UNCLEAR ? formatIshiharaAnswer(entry) : entry), [entry]);

  if (step === 'results') return <ColourVisionResults localResults={answers} setActiveTab={setActiveTab} onRestart={restart} />;
  if (!plate) return null;

  return (
    <StepBody data-testid='colour-vision' data-plate={plate.id}>
      <Layout>
        <Plate src={ishiharaPlateImage(plate.id)} alt={`Ishihara plate ${plate.id}`} />
        <Side>
          <Progress>{`Plate ${index + 1} of ${plates.length}${index === 0 ? ' · practice' : ''}`}</Progress>
          {keypad ? (
            <Keypad title='Current Characters' value={entryLabel} onKey={onKey} />
          ) : (
            <>
              <Label size={18} weight={700} align='center'>
                Patient Answer
              </Label>
              <ToggleGroup
                ariaLabel='Patient answer'
                options={[...plate.options, { value: ISHIHARA_UNCLEAR, label: 'Nothing seen' }]}
                value={entry}
                onChange={setEntry}
              />
            </>
          )}
          {plate.custom_instructions && <Note align='center'>{plate.custom_instructions}</Note>}
        </Side>
      </Layout>
      <SessionActions onBack={back} primary={{ label: index + 1 >= plates.length ? 'Finish' : 'Next', onClick: record, disabled: entry === '' }} />
    </StepBody>
  );
};

export default ColourVision;
